import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search, FileText, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Staff, ExportFormat } from '@/services/school.api';
import { ReportsApi } from '@/services/reports.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { formatMoney } from '@/lib/format';

const columnHelper = createColumnHelper<Staff>();

const BASE_COLUMNS = [
  columnHelper.accessor('employeeId', { header: 'Employee ID' }),
  columnHelper.display({ id: 'name', header: 'Name', cell: (ctx) => ctx.row.original.user?.name ?? '(no linked user)' }),
  columnHelper.display({ id: 'gender', header: 'Gender', cell: (ctx) => ctx.row.original.user?.gender ?? '—' }),
  columnHelper.display({ id: 'email', header: 'Email', cell: (ctx) => ctx.row.original.user?.email ?? '—' }),
  columnHelper.accessor('designation', { header: 'Designation' }),
  columnHelper.accessor('department', { header: 'Department', cell: (ctx) => ctx.getValue() ?? '—' }),
  columnHelper.accessor('salary', { header: 'Salary', cell: (ctx) => formatMoney(ctx.getValue()) }),
  columnHelper.accessor('address', { header: 'Address', cell: (ctx) => ctx.getValue() ?? '—' }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (ctx) => <Badge tone={ctx.getValue() === 'active' ? 'green' : 'slate'}>{ctx.getValue()}</Badge>,
  }),
];

/**
 * Teachers Report — checked the legacy blade/controller directly
 * (`teachers_report.blade.php` / `StateReportController::teachersReport`):
 * it's the staff roster (name/salary/gender/email/address) scoped to
 * role=teacher, no distinct aggregate. Reuses the existing Staff list
 * query (StaffService.list()) via a Reports-nav entry point instead of
 * building a parallel query.
 */
export function TeachersReportPage() {
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'name', defaultSortOrder: 'asc' });
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    ...BASE_COLUMNS,
  ];

  const { data, isFetching } = useQuery({
    queryKey: ['reports', 'teachers', dt.params],
    queryFn: () => ReportsApi.teachersReport(dt.params),
    placeholderData: keepPreviousData,
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await ReportsApi.exportTeachers(format);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Teachers Report"
        description="Teaching staff — name, gender, contact, salary, and address."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('csv')}
              disabled={!!busy}
              title="Export to CSV"
            >
              {busy === 'csv' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-slate-600" />}
              CSV
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name or employee ID"
            className="pl-9"
          />
        </div>
      </div>

      <DataTable columns={columns} result={data} state={dt} loading={isFetching} emptyMessage="No teachers found." getRowId={(row) => row.id} />
    </>
  );
}
